// Card 66, the map markers (R1291, R1294, ui-map-interface.md §17.3.66).
//
// A commander puts a marker on a cell for the squad or for the faction:
// attack, defend, gather or danger (player-reach.md §2). The hub sends the
// whole list this client may see in every Markers frame, and the state
// store holds it. This piece draws every marker of the list at its cell in
// the look the player picked (the Marks field `mapMarker`), with the owner's
// name under it in the mono map face, on the halo of R586.
//
// **THE LOOK IS NOT THE READING.** The five looks are mockup round 062,
// cards 01, 02, 03, 05 and 10. Both chart readings draw the same look,
// because the look is a Marks pick and the reading is a chart setting.
//
// **TWO OBJECTS, ONE PIECE.** The registry holds `map_markers` twice, as it
// holds card 18 twice. The STATIC object draws what changes only with the
// list, the look and an age step: the pin, the pennant, the pencil, the
// discs and every name. The LIVE object draws what moves on every frame:
// the ring of the pulse look and the column of the flare look. A cheap
// look therefore costs one flush a frame, and only the two per-frame looks
// pack on every frame (§10.3).
//
// **THE LIFE.** A marker's age runs from 0 when it is new to 1 at its end:
// the share of `CatalogRules.marker_life_sec` that `expires_unix` has used
// up on the wall clock. The static object packs again when the age of the
// list passes one of `age_steps` steps and when a marker's life ends, and
// on no other clock.

import { MAP_MARKER_STYLES } from '../../../gen/balance';
import { LOCALE, markers as SM } from '../../../gen/strings';
import type { MapMarker } from '../../../gen/proto/aquaphobia';
import { reducedMotion } from '../../../render/motion';
import { markerCellKey, type MarkerFig } from '../../../render/map-marker-cells';
import {
  LIVE_LOOKS, PENCIL_SEED2_OFFSET, PENCIL_SEED_OFFSET,
  audienceSlug, kindSlug, markerAge, markerExpired, pencilLoop,
  type MarkerAudienceSlug, type MarkerKindSlug,
} from '../../../render/map-markers';
import type { MapMarkerStyle } from '../../../ui/settings';
import { cellCenter } from '../../../world';
import { CHART_ASCENT_EM, CHART_LINE_EM } from '../../gfx/atlas';
import type { FrameInfo } from '../../layer';
import type { SpriteEntry } from '../../layers/batches';
import type { ChartContext, ChartSinks, Piece, RebuildReason } from '../context';
import { stepPx } from '../steps';
import { CHART_INK, offCull } from './shared';
import { clamp01 } from '../../../render/math';

/** One marker as the piece draws it. */
export interface DrawnMarker {
  id: number;
  x: number;
  y: number;
  kind: MarkerKindSlug;
  aud: MarkerAudienceSlug;
  /** 0 when new, 1 at the end of its life. */
  age: number;
  /** The owner's name, as the label writes it. */
  name: string;
  /** The seed of the pencil wobble and the pulse phase: the marker's id. */
  seed: number;
  expires: number;
}

/** The AUTO look, which a scene that states none draws (R1291). */
const AUTO_LOOK: MapMarkerStyle = MAP_MARKER_STYLES[0];

/** How far outside the visible rect a live mark still packs, in px. The
 *  static half culls nothing, because its pack outlives a pan (R2238). */
const CULL_MARGIN = 80;

/** The white of a squad mark, which the cards draw every squad part in. */
const WHITE = 0xffffff;

/**
 * The pulse phase of round 062: the seed spreads the markers of one list
 * apart in time, `SEED_PHASE_S` a seed. Reduced motion holds the phase at
 * the card's still frame.
 */
const SEED_PHASE_S = 0.7;
const STILL_PHASE = 0.45;
/** The flare flicker of round 062: the seed shifts it, 2.1 rad a seed. */
const SEED_FLICKER = 2.1;
/** The flare column starts this far over the cell, as the card drew it. */
const COLUMN_FOOT = 2;

/** The ink weights of the cards, named for what they paint. */
const W = {
  /** The pale staff of the pennant. */
  staff: 0.9,
  /** The white dot at the foot of the staff and of the flare. */
  dot: 0.95,
  /** The second loop of a defend pencil, against the first. */
  loop2: 0.8,
  /** The underline or the box of the pencil word, against the pencil. */
  rule: 0.8,
} as const;

/**
 * The pencil strokes of card 05, in world px at the card's loop radius of
 * 13. The piece scales them by `pencil_r / 13`, so a tuned loop keeps its
 * arrow, its ticks and its word where the card put them.
 */
const PENCIL_CARD_R = 13;
const PENCIL = {
  /** The attack arrow into the loop: the shaft and the two barbs. */
  arrow: [[30, -18, 15, -8, 1], [15, -8, 21, -9, 1.2 / 1.3], [15, -8, 18, -3, 1.2 / 1.3]],
  /** The three gather ticks: angle, then the outer and inner radii. */
  ticks: [0.6, 1.3, 2.4],
  tickOut: [22, 20],
  tickIn: [16, 15],
  /** The danger cross under the loop. */
  cross: [[-16, 20, -10, 15], [-16, 15, -10, 20]],
  /** The second loop's stroke, against the first (1.1 against 1.3). */
  loop2Width: 1.1 / 1.3,
  /** Where the word starts: the attack word stands clear of the arrow. */
  word: { attack: [26, -20], other: [18, -14] },
  /** The underline of a squad word, from its left end to its right. */
  underline: [2.5, 2],
  /** The box of a faction word: the pad left and right, the top, the height. */
  box: { pad: 2, top: 9, h: 12 },
} as const;

/** The pencil word of each kind, in the reader's language. */
const PENCIL_WORD: Record<MarkerKindSlug, string> = {
  attack: SM.pencil_attack,
  defend: SM.pencil_defend,
  gather: SM.pencil_gather,
  danger: SM.pencil_danger,
};

/** The `fig` key that says how far under the mark each look writes the name. */
const LABEL_DY: Record<MapMarkerStyle, keyof MarkerFig> = {
  'map-pin': 'pin_label_dy',
  pennant: 'flag_label_dy',
  pencil: 'pencil_label_dy',
  'flare-column': 'flare_label_dy',
  'pulse-ring': 'pulse_label_dy',
};

/** What one look writes into the static pack for one marker. */
type LookEmit = (sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker, f: MarkerFig) => void;

/**
 * The static part of each look. The flare's head rides its column, so the
 * live object draws it and the static object writes the flare's name alone.
 */
const STATIC_LOOK: Record<MapMarkerStyle, LookEmit | null> = {
  'map-pin': emitPin,
  pennant: emitPennant,
  pencil: emitPencil,
  'pulse-ring': emitPulseDisc,
  'flare-column': null,
};

/** A pencil line that is not yet broken into dashes. */
const SOLID: readonly number[] = [];

/**
 * The list both objects draw from, built one time for both.
 *
 * It keeps the age step and the first end of a life it built at, so the
 * static object can ask "is my pack still the picture?" with two compares.
 */
export class MapMarkerModel {
  /** The markers of the list, oldest first. The tests read them. */
  list: DrawnMarker[] = [];
  /** The look the list is drawn in. */
  look: MapMarkerStyle = AUTO_LOOK;
  /** Reduced motion, read at the build and not on every frame. */
  still = false;
  private step = -1;
  private nextEnd = Infinity;
  private source: readonly MapMarker[] | null = null;
  private lookSeen: MapMarkerStyle | null = null;

  constructor(private readonly nowSec: () => number = () => Date.now() / 1000) {}

  /** The wall clock the model reads, in seconds. */
  now(): number { return this.nowSec(); }

  /** The age step of the list at a wall second. */
  private stepAt(ctx: ChartContext, now: number): number {
    const life = ctx.state.rules.markerLifeSec;
    const steps = ctx.cfg.pieces.map_markers.fig.age_steps;
    if (!(life > 0) || !(steps > 0)) return 0;
    return Math.floor(now / (life / steps));
  }

  /** Builds the list from the state, unless it already stands for it. */
  build(ctx: ChartContext): void {
    const now = this.nowSec();
    const look = ctx.scene.mapMarker ?? AUTO_LOOK;
    const src = ctx.state.mapMarkers;
    if (src === this.source && look === this.lookSeen && !this.outgrown(ctx)) return;
    this.source = src;
    this.lookSeen = look;
    this.look = look;
    this.still = reducedMotion();
    this.step = this.stepAt(ctx, now);
    this.nextEnd = Infinity;
    const life = ctx.state.rules.markerLifeSec;
    const out: DrawnMarker[] = [];
    for (const m of src) {
      const kind = kindSlug(m.kind);
      if (kind === null || markerExpired(m, now)) continue;
      if (m.expiresUnix > 0) this.nextEnd = Math.min(this.nextEnd, m.expiresUnix);
      const [x, y] = cellCenter(m.cell);
      out.push({
        id: m.id, x, y, kind, aud: audienceSlug(m.audience),
        age: markerAge(m.expiresUnix, life, now),
        name: m.ownerName.toLocaleUpperCase(LOCALE),
        seed: m.id,
        expires: m.expiresUnix,
      });
    }
    this.list = out;
  }

  /** True when an age step passed or a life ended since the build. */
  outgrown(ctx: ChartContext): boolean {
    const now = this.nowSec();
    return now >= this.nextEnd || this.stepAt(ctx, now) !== this.step;
  }
}

/** The static half: every part that moves only with an age step. */
export class MapMarkersPiece implements Piece {
  readonly id = 'map_markers';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn: readonly RebuildReason[] = ['hello', 'markers', 'look', 'font'];

  constructor(readonly model: MapMarkerModel) {}

  visible(ctx: ChartContext): boolean {
    return ctx.state.mapMarkers.length > 0;
  }

  rebuild(ctx: ChartContext): void { this.model.build(ctx); }

  outgrown(ctx: ChartContext): boolean { return this.model.outgrown(ctx); }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const f = ctx.cfg.pieces.map_markers.fig;
    const look = this.model.look;
    const draw = STATIC_LOOK[look];
    const dy = f[LABEL_DY[look]];
    for (const m of this.model.list) {
      draw?.(sinks, ctx, m, f);
      emitName(sinks, ctx, m, f, dy);
    }
  }
}

/**
 * The live half: the pulse ring and the flare column, on every frame, and
 * the one white ring of a quick line (R1607, mockup round 077, card 04).
 * The quick ring rides this object because it is a message of the same
 * audience as a marker: it draws under every chart setting, and only while
 * a ring is on the glass does it cost a frame (§10.3).
 */
export class MapMarkersLivePiece implements Piece {
  readonly id = 'map_markers';
  readonly tier = 'screen';
  readonly pack = 'live';
  readonly film = 'hide';
  readonly rebuildOn: readonly RebuildReason[] = ['hello', 'markers', 'look'];

  constructor(readonly model: MapMarkerModel) {}

  visible(ctx: ChartContext): boolean {
    return (ctx.state.mapMarkers.length > 0 && LIVE_LOOKS.has(ctx.scene.mapMarker ?? AUTO_LOOK))
      || ctx.state.quickPings.length > 0;
  }

  rebuild(ctx: ChartContext): void { this.model.build(ctx); }

  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void {
    const f = ctx.cfg.pieces.map_markers.fig;
    // The motion query is read only while a ring stands (§10.3).
    if (ctx.state.quickPings.length > 0) {
      emitQuickRings(sinks, ctx, f, frame, this.model.now() * 1000, reducedMotion());
    }
    const look = this.model.look;
    if (!LIVE_LOOKS.has(look) || ctx.state.mapMarkers.length === 0) return;
    const life = ctx.state.rules.markerLifeSec;
    const now = this.model.now();
    const t = frame.timeS;
    for (const m of this.model.list) {
      if (offCull(frame.cull, m.x, m.y, CULL_MARGIN)) continue;
      // The live age, so a ring slows and a column sinks smoothly.
      const age = markerAge(m.expires, life, now);
      if (look === 'pulse-ring') emitPulse(sinks, m, f, age, t, this.model.still);
      else emitFlare(sinks, ctx, m, f, age, t, this.model.still);
    }
  }
}

/**
 * The rings of the quick lines (R1607, card 04): one white ring at each
 * point a quick line named, widening from `quick_ring_r0` by
 * `quick_ring_reach` and fading over `quick_ring_sec` wall seconds. Reduced
 * motion holds each ring at the share `quick_ring_still` of its way until
 * its time is up. A ring whose time is up leaves the list here.
 */
function emitQuickRings(
  sinks: ChartSinks, ctx: ChartContext, f: MarkerFig, frame: FrameInfo, nowMs: number, still: boolean,
): void {
  const lifeMs = f.quick_ring_sec * 1000;
  ctx.state.pruneQuickPings(nowMs, lifeMs);
  for (const p of ctx.state.quickPings) {
    const [x, y] = cellCenter(p.cell);
    if (offCull(frame.cull, x, y, CULL_MARGIN + f.quick_ring_reach)) continue;
    const k = still ? f.quick_ring_still : clamp01((nowMs - p.at) / lifeMs);
    sinks.circles.ring(x, y, f.quick_ring_r0 + f.quick_ring_reach * k, f.quick_ring_width,
      WHITE, f.quick_ring_alpha * (1 - k));
  }
}

// ---------------------------------------------------------------------------
// The looks
// ---------------------------------------------------------------------------

/**
 * The owner's name, centred under the mark in the mono map face. It fades
 * by `label_age_fade` over the life, as every card drew it.
 */
function emitName(sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker, f: MarkerFig, dy: number): void {
  const step = ctx.cfg.pieces.map_markers.text.name;
  const size = stepPx('mono', step);
  // The card wrote the name from its TOP; a run is laid on its line centre.
  // The name grows about that top, so it never climbs over the mark.
  const top = m.y + dy;
  ctx.glyphs.word(sinks.quads, m.name, m.x, top + (size * CHART_LINE_EM) / 2, 'mono', step, CHART_INK.pale,
    f.label_alpha * (1 - f.label_age_fade * m.age), { anchor: 'centre', pivot: { x: m.x, y: top } });
}

/** Card 01: the pin fades from full ink to a third; a squad glyph keeps its cut. */
function emitPin(sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker, f: MarkerFig): void {
  const a = 1 - f.pin_fade * m.age;
  const body = ctx.glyphs.mark(markerCellKey('pin', null, m.aud));
  const glyph = ctx.glyphs.mark(markerCellKey('pinglyph', m.kind, m.aud));
  sinks.quads.sprite(m.x, m.y, 0, body, WHITE, a, 1, 0, false);
  sinks.quads.sprite(m.x, m.y, 0, glyph, WHITE, m.aud === 'squad' ? 1 : a, 1, 0, false);
}

/** Card 02: the flag comes down the staff over the life. */
function emitPennant(sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker, f: MarkerFig): void {
  sinks.segments.line(m.x, m.y, m.x, m.y - f.flag_staff, f.flag_staff_width, CHART_INK.pale, W.staff);
  sinks.circles.disc(m.x, m.y, f.flag_dot_r, WHITE, W.dot);
  const fy = -f.flag_staff + f.flag_drop * m.age;
  const flag = ctx.glyphs.mark(markerCellKey('flag', m.kind, m.aud));
  sinks.quads.sprite(m.x, m.y + fy, 0, flag, WHITE, 1, 1, 0, false);
}

/** Card 03, the disc under the ring: the ring itself is the live object's. */
function emitPulseDisc(sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker): void {
  sinks.quads.sprite(
    m.x, m.y, 0, ctx.glyphs.mark(markerCellKey('pulse', m.kind, m.aud)), WHITE, 1, 1, 0, false,
  );
}

/** One open polyline, solid or in the pencil dash. */
function polyline(
  sinks: ChartSinks, pts: readonly (readonly [number, number])[], ox: number, oy: number,
  width: number, color: number, alpha: number, dash: readonly number[],
): void {
  const on = dash[0] ?? 0, off = dash[1] ?? 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    sinks.segments.line(ox + a[0], oy + a[1], ox + b[0], oy + b[1], width, color, alpha, 0, on, off);
  }
}

/**
 * Card 05: a hand-drawn loop round the cell and the kind as a pencilled
 * word. The stroke says the kind a second time: an arrow for attack, a
 * second loop for defend, three ticks for gather, a jagged loop and a cross
 * for danger. A squad word is underlined and a faction word is boxed. The
 * pencil fades, and near the end of its life the line breaks into dashes.
 */
function emitPencil(sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker, f: MarkerFig): void {
  const k = f.pencil_r / PENCIL_CARD_R;
  const a = f.pencil_alpha - f.pencil_alpha_age * m.age;
  const dash = m.age > f.pencil_dash_age ? ctx.cfg.pieces.map_markers.arr.pencil_dash : SOLID;
  const ink = CHART_INK.pale;
  const w = f.pencil_width;
  polyline(sinks, pencilLoop(f.pencil_r, m.seed + PENCIL_SEED_OFFSET, m.kind === 'danger'),
    m.x, m.y, w, ink, a, dash);
  if (m.kind === 'defend') {
    polyline(sinks, pencilLoop(f.pencil_r2, m.seed + PENCIL_SEED2_OFFSET, false),
      m.x, m.y, w * PENCIL.loop2Width, ink, a * W.loop2, dash);
  }
  if (m.kind === 'attack') {
    for (const [x0, y0, x1, y1, ww] of PENCIL.arrow) {
      sinks.segments.line(m.x + x0 * k, m.y + y0 * k, m.x + x1 * k, m.y + y1 * k, w * ww, ink, a);
    }
  }
  if (m.kind === 'gather') {
    for (const th of PENCIL.ticks) {
      sinks.segments.line(
        m.x + Math.cos(th) * PENCIL.tickOut[0] * k, m.y + Math.sin(th) * PENCIL.tickOut[1] * k,
        m.x + Math.cos(th) * PENCIL.tickIn[0] * k, m.y + Math.sin(th) * PENCIL.tickIn[1] * k,
        w, ink, a);
    }
  }
  if (m.kind === 'danger') {
    for (const [x0, y0, x1, y1] of PENCIL.cross) {
      sinks.segments.line(m.x + x0 * k, m.y + y0 * k, m.x + x1 * k, m.y + y1 * k, w, ink, a);
    }
  }
  // The word, set on its baseline as the card set it: a run is laid on its
  // line centre, which stands this far over the baseline. The word is a part
  // of the pencil drawing, with its underline or its box, so it takes the
  // size of its step and grows with the water as the loop does (R1796).
  const size = stepPx('fell', ctx.cfg.pieces.map_markers.text.pencil);
  const [wx0, wy0] = m.kind === 'attack' ? PENCIL.word.attack : PENCIL.word.other;
  const wx = m.x + wx0 * k, wy = m.y + wy0 * k;
  const centre = wy - size * (CHART_ASCENT_EM - CHART_LINE_EM / 2);
  const tw = ctx.glyphs.run(sinks.quads, PENCIL_WORD[m.kind], wx, centre, 'fell', size, ink, a);
  const on = dash[0] ?? 0, off = dash[1] ?? 0;
  const ra = a * W.rule;
  if (m.aud === 'squad') {
    sinks.segments.line(wx, wy + PENCIL.underline[0], wx + tw, wy + PENCIL.underline[1],
      1, ink, ra, 0, on, off);
    return;
  }
  const bx0 = wx - PENCIL.box.pad, bx1 = wx + tw + PENCIL.box.pad;
  const by0 = wy - PENCIL.box.top, by1 = by0 + PENCIL.box.h;
  polyline(sinks, [[bx0, by0], [bx1, by0], [bx1, by1], [bx0, by1], [bx0, by0]], 0, 0, 1, ink, ra, dash);
}

/**
 * Card 03, the ring: one ring leaves the disc every period and fades as it
 * widens. Over the life the period grows, and the reach and the alpha fall.
 */
function emitPulse(
  sinks: ChartSinks, m: DrawnMarker, f: MarkerFig, age: number, t: number, still: boolean,
): void {
  const period = f.pulse_period_sec + f.pulse_period_age_sec * age;
  const u = still ? STILL_PHASE : (((t + m.seed * SEED_PHASE_S) / period) % 1 + 1) % 1;
  const reach = f.pulse_reach - f.pulse_reach_age * age;
  const alpha = (1 - u) * f.pulse_alpha * (1 - f.pulse_alpha_age * age);
  sinks.circles.ring(m.x, m.y, f.disc_r + u * reach, f.ring_width,
    m.aud === 'squad' ? WHITE : CHART_INK.accent, alpha);
}

/**
 * Card 10: a column of light stands on the cell with the disc on top. The
 * column flickers, and over the life it grows shorter and fainter. A squad
 * column is one broad stroke; a faction column is two hairlines.
 */
function emitFlare(
  sinks: ChartSinks, ctx: ChartContext, m: DrawnMarker, f: MarkerFig,
  age: number, t: number, still: boolean,
): void {
  const h = f.flare_h - f.flare_h_age * age;
  const flick = still
    ? 1
    : 1 - f.flare_flicker + f.flare_flicker * Math.sin(t * f.flare_flicker_rate + m.seed * SEED_FLICKER);
  const a = (f.flare_alpha - f.flare_alpha_age * age) * flick;
  const col = ctx.glyphs.mark(markerCellKey('column', null, null));
  const foot = m.y - COLUMN_FOOT;
  const len = Math.max(0, h - COLUMN_FOOT);
  if (m.aud === 'squad') column(sinks, m.x, foot, len, f.flare_width, col, a);
  else {
    column(sinks, m.x - f.flare_gap, foot, len, f.flare_width_hollow, col, a);
    column(sinks, m.x + f.flare_gap, foot, len, f.flare_width_hollow, col, a);
  }
  sinks.circles.disc(m.x, m.y, f.flare_dot_r, WHITE, W.dot * a);
  const head = ctx.glyphs.mark(markerCellKey('head', m.kind, m.aud));
  sinks.quads.sprite(m.x, m.y - h - f.flare_head_dy, 0, head, WHITE, 1, 1, 0, false);
}

/**
 * One column of the flare, `len` px up from its foot over the cell. The
 * white cell is stretched to it, and the quad carries the accent and the
 * alpha, so the gradient of the card runs the whole length.
 */
function column(
  sinks: ChartSinks, x: number, foot: number, len: number, width: number,
  col: SpriteEntry, alpha: number,
): void {
  sinks.quads.quad(
    x, foot - len / 2, 0, 0, width, len, 0, 0, CHART_INK.accent, alpha,
    col.u0, col.v0, col.u1, col.v1,
  );
}

/** The one model both objects share, and the two registry entries. */
const model = new MapMarkerModel();
export const mapMarkers = new MapMarkersPiece(model);
export const mapMarkersLive = new MapMarkersLivePiece(model);
